/**
 * Burndown Chart Component
 * Visualizes sprint progress with ideal vs actual burndown using Recharts
 */

import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { fetchBurndownChart } from '../../store/sprintSlice';
import dayjs from 'dayjs';
import './BurndownChart.scss';

interface BurndownChartProps {
  sprintId: string;
}

type ChartType = 'line' | 'area';

const BurndownChart: React.FC<BurndownChartProps> = ({ sprintId }) => {
  const dispatch = useAppDispatch();
  const { burndownData, loading } = useAppSelector((state) => state.sprints);
  const [chartType, setChartType] = useState<ChartType>('area');

  useEffect(() => {
    if (sprintId) {
      dispatch(fetchBurndownChart(sprintId));
    }
  }, [sprintId, dispatch]);

  const formattedData = useMemo(() => {
    if (!burndownData || !Array.isArray(burndownData)) return [];
    return burndownData.map((dp: any) => ({
      ...dp,
      ideal: dp.ideal !== undefined ? dp.ideal : (dp.idealPoints !== undefined ? dp.idealPoints : dp.idealTasks || 0),
      actual: dp.actual !== undefined ? dp.actual : (dp.remainingPoints !== undefined ? dp.remainingPoints : dp.remainingTasks || 0),
      scope: dp.scope !== undefined ? dp.scope : (dp.scopePoints || 0),
    }));
  }, [burndownData]);

  const handleChartTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newType: ChartType | null
  ) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MMM D');
  };

  const calculateTrend = (): {
    status: 'ahead' | 'on-track' | 'behind';
    difference: number;
  } => {
    if (!formattedData || formattedData.length === 0) {
      return { status: 'on-track', difference: 0 };
    }

    const latestData = formattedData[formattedData.length - 1];
    const difference = latestData.actual - latestData.ideal;

    if (difference < -5) {
      return { status: 'ahead', difference };
    } else if (difference > 5) {
      return { status: 'behind', difference };
    } else {
      return { status: 'on-track', difference };
    }
  };

  const trend = calculateTrend();

  const getTrendColor = () => {
    switch (trend.status) {
      case 'ahead':
        return '#4caf50';
      case 'behind':
        return '#f44336';
      case 'on-track':
      default:
        return '#2196f3';
    }
  };

  const getTrendIcon = () => {
    switch (trend.status) {
      case 'ahead':
        return <TrendingDownIcon />;
      case 'behind':
        return <TrendingUpIcon />;
      case 'on-track':
      default:
        return <TimelineIcon />;
    }
  };

  const getTrendMessage = () => {
    switch (trend.status) {
      case 'ahead':
        return `${Math.abs(trend.difference).toFixed(0)} points ahead of schedule`;
      case 'behind':
        return `${trend.difference.toFixed(0)} points behind schedule`;
      case 'on-track':
      default:
        return 'On track with ideal burndown';
    }
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box className="custom-tooltip">
          <Typography variant="subtitle2" className="tooltip-date">
            {formatDate(payload[0].payload.date)}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} className="tooltip-entry">
              <Box
                className="tooltip-dot"
                sx={{ backgroundColor: entry.color }}
              />
              <Typography variant="body2" className="tooltip-label">
                {entry.name}: {entry.value} points
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card className="burndown-chart-card">
        <CardContent className="loading-content">
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading burndown data...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!formattedData || formattedData.length === 0) {
    return (
      <Card className="burndown-chart-card">
        <CardContent className="empty-content">
          <TimelineIcon fontSize="large" color="disabled" />
          <Typography variant="body1" color="text.secondary">
            No burndown data available yet
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Start the sprint and complete tasks to see the burndown chart
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="burndown-chart-card">
      <CardContent>
        <Box className="chart-header">
          <Box className="header-left">
            <TimelineIcon color="primary" className="chart-icon" />
            <Typography variant="h6" className="chart-title">
              Sprint Burndown
            </Typography>
          </Box>

          <Box className="header-right">
            <Chip
              icon={getTrendIcon()}
              label={getTrendMessage()}
              className={`trend-chip trend-${trend.status}`}
              sx={{ backgroundColor: `${getTrendColor()}15`, color: getTrendColor() }}
            />
            <ToggleButtonGroup
              value={chartType}
              exclusive
              onChange={handleChartTypeChange}
              size="small"
              className="chart-type-toggle"
            >
              <ToggleButton value="area">Area</ToggleButton>
              <ToggleButton value="line">Line</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        <Box className="chart-container">
          <ResponsiveContainer width="100%" height={400}>
            {chartType === 'area' ? (
              <AreaChart
                data={formattedData}
                margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
              >
                <defs>
                  <linearGradient id="idealGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2196f3" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#2196f3" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4caf50" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#4caf50" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="scopeGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff9800" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ff9800" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  stroke="#5e6c84"
                  style={{ fontSize: 12 }}
                />
                <YAxis
                  stroke="#5e6c84"
                  style={{ fontSize: 12 }}
                  label={{
                    value: 'Story Points',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 12, fill: '#5e6c84' },
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 14 }}
                  iconType="line"
                />
                <Area
                  type="monotone"
                  dataKey="ideal"
                  stroke="#2196f3"
                  strokeWidth={2}
                  fill="url(#idealGradient)"
                  name="Ideal Burndown"
                />
                <Area
                  type="monotone"
                  dataKey="actual"
                  stroke="#4caf50"
                  strokeWidth={3}
                  fill="url(#actualGradient)"
                  name="Actual Burndown"
                />
                <Area
                  type="monotone"
                  dataKey="scope"
                  stroke="#ff9800"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  fill="url(#scopeGradient)"
                  name="Scope Changes"
                />
              </AreaChart>
            ) : (
              <LineChart
                data={formattedData}
                margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  stroke="#5e6c84"
                  style={{ fontSize: 12 }}
                />
                <YAxis
                  stroke="#5e6c84"
                  style={{ fontSize: 12 }}
                  label={{
                    value: 'Story Points',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 12, fill: '#5e6c84' },
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 14 }}
                  iconType="line"
                />
                <Line
                  type="monotone"
                  dataKey="ideal"
                  stroke="#2196f3"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Ideal Burndown"
                />
                <Line
                  type="monotone"
                  dataKey="actual"
                  stroke="#4caf50"
                  strokeWidth={3}
                  dot={{ r: 5 }}
                  name="Actual Burndown"
                />
                <Line
                  type="monotone"
                  dataKey="scope"
                  stroke="#ff9800"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ r: 4 }}
                  name="Scope Changes"
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </Box>

        {/* Chart Legend Explanation */}
        <Box className="chart-legend-info">
          <Box className="legend-item">
            <Box className="legend-dot ideal" />
            <Typography variant="caption">
              <strong>Ideal Burndown:</strong> Expected progress if work is completed evenly
            </Typography>
          </Box>
          <Box className="legend-item">
            <Box className="legend-dot actual" />
            <Typography variant="caption">
              <strong>Actual Burndown:</strong> Real progress based on completed tasks
            </Typography>
          </Box>
          <Box className="legend-item">
            <Box className="legend-dot scope" />
            <Typography variant="caption">
              <strong>Scope Changes:</strong> Impact of added/removed tasks
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BurndownChart;
